// src/pages/StorePage.jsx
import React from "react";
import MerchCard from "../components/MerchCard";
import api from "../lib/api";

function normalizeAffiliate(item) {
  const title =
    item.title ||
    item.name ||
    item.productTitle ||
    item.label ||
    "Untitled item";

  const url =
    item.url ||
    item.link ||
    item.detailPageURL ||
    item.productUrl ||
    item.detail_page_url ||
    "";

  const image =
    item.image ||
    item.img ||
    item.imageUrl ||
    item.thumbnail ||
    item.thumbnail_url ||
    item.smallImage ||
    "";

  const price =
    item.price ||
    item.priceText ||
    item.formattedPrice ||
    item.price_display ||
    "";

  return { title, url, image, price };
}

function normalizeTitle(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getPrintfulCategory(product) {
  const name = normalizeTitle(product?.name);

  const isLolo =
    name.includes("lo go loco") ||
    name.includes("lo_go_loco") ||
    name.includes("lologo") ||
    name.includes("lolo");

  if (isLolo) return "Lolo Products";

  const isGetActiveGear =
    name.includes("getactive") ||
    name.includes("get active") ||
    name.includes("ga hoodie") ||
    name.includes("hoodie");

  if (isGetActiveGear) return "GetActive Gear";

  return "Graphic Tees";
}

function buildPrintfulGroups(products) {
  const orderedLabels = ["Lolo Products", "Graphic Tees", "GetActive Gear"];
  const buckets = new Map(orderedLabels.map((label) => [label, []]));

  for (const product of products) {
    const label = getPrintfulCategory(product);
    if (!buckets.has(label)) buckets.set(label, []);
    buckets.get(label).push(product);
  }

  const groups = orderedLabels
    .map((label) => ({
      label,
      items: [...(buckets.get(label) || [])].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""))
      ),
    }))
    .filter((group) => group.items.length > 0);

  for (const [label, items] of buckets.entries()) {
    if (orderedLabels.includes(label) || !items.length) continue;
    groups.push({
      label,
      items: [...items].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""))
      ),
    });
  }

  return groups;
}

function SectionHeading({ children, subtext }) {
  return (
    <div style={{ margin: "8px 0 12px" }}>
      <div
        style={{
          color: "#dfe8ff",
          fontWeight: 800,
          fontSize: "1.2rem",
          letterSpacing: "0.02em",
        }}
      >
        {children}
      </div>
      {subtext ? (
        <div
          style={{
            color: "#9aa7c6",
            fontSize: "0.92rem",
            marginTop: 4,
          }}
        >
          {subtext}
        </div>
      ) : null}
    </div>
  );
}

function CategoryCarousel({ title, items }) {
  const [index, setIndex] = React.useState(0);
  const total = items.length;

  React.useEffect(() => {
    if (index > total - 1) setIndex(0);
  }, [index, total]);

  if (!total) return null;

  const current = items[index];
  const prev = () => setIndex((n) => (n - 1 + total) % total);
  const next = () => setIndex((n) => (n + 1) % total);
  const showControls = total > 1;

  return (
    <section
      className="store-category-shell"
      style={{
        position: "relative",
        borderRadius: 22,
        padding: 14,
        background: "rgba(0, 0, 0, 0.55)",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              color: "#ecf2ff",
              fontWeight: 800,
              fontSize: "1.02rem",
            }}
          >
            {title}
          </div>
          <div
            style={{
              color: "#9aa7c6",
              fontSize: "0.86rem",
              marginTop: 2,
            }}
          >
            {total} item{total === 1 ? "" : "s"}
          </div>
        </div>

        {showControls ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#9aa7c6",
              fontWeight: 700,
              fontSize: "0.9rem",
            }}
          >
            <span>
              {index + 1} / {total}
            </span>
          </div>
        ) : null}
      </div>

      <div style={{ position: "relative" }}>
        <div
          style={{
            maxWidth: 360,
            margin: "0 auto",
          }}
        >
          <MerchCard key={current.id || current.external_id || `${title}-${index}`} product={current} />
        </div>

        {showControls ? (
          <>
            <button
              type="button"
              aria-label={`Previous ${title} product`}
              onClick={prev}
              className="store-category-arrow store-category-arrow-left"
              style={arrowStyle("left")}
            >
              ‹
            </button>
            <button
              type="button"
              aria-label={`Next ${title} product`}
              onClick={next}
              className="store-category-arrow store-category-arrow-right"
              style={arrowStyle("right")}
            >
              ›
            </button>
          </>
        ) : null}
      </div>
    </section>
  );
}

function arrowStyle(side) {
  return {
    position: "absolute",
    top: "50%",
    [side]: 8,
    transform: "translateY(-50%)",
    width: 42,
    height: 42,
    borderRadius: "999px",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(10, 16, 28, 0.82)",
    color: "#ffffff",
    fontSize: 28,
    lineHeight: 1,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    zIndex: 2,
    opacity: 0.18,
    transition: "opacity 160ms ease, transform 160ms ease, background 160ms ease",
    boxShadow: "0 10px 22px rgba(0,0,0,0.35)",
  };
}

export default function StorePage() {
  const [affiliates, setAffiliates] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const [a, p] = await Promise.all([
          api.affiliates(),
          api.merch.products(),
        ]);

        if (!alive) return;

        if (a.ok) {
          const items = Array.isArray(a.data?.items) ? a.data.items : [];
          setAffiliates(items);
        }
        if (p.ok) {
          const items = Array.isArray(p.data?.items) ? p.data.items : [];
          setProducts(items);
        }

        if (!a.ok || !p.ok) {
          setErr("load_failed");
        }
      } catch (e) {
        if (!alive) return;
        console.error("Store load failed", e);
        setErr("load_failed");
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const merchGroups = React.useMemo(() => buildPrintfulGroups(products), [products]);

  const gridAff = {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
    marginTop: 12,
  };

  const categoryGrid = {
    display: "grid",
    gap: 18,
    gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
    marginTop: 12,
  };

  const cardBase = {
    borderRadius: 18,
    padding: 10,
    background: "rgba(0, 0, 0, 0.6)",
    border: "1px solid rgba(255, 255, 255, 0.04)",
    boxShadow: "0 8px 18px rgba(0, 0, 0, 0.65)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    textDecoration: "none",
    color: "#ecf2ff",
    transition:
      "transform 150ms ease, box-shadow 150ms ease, background 150ms ease",
  };

  return (
    <main className="page" style={{ padding: "16px 16px 40px" }}>
      <style>{`
        .store-category-shell:hover .store-category-arrow {
          opacity: 0.95 !important;
        }
        .store-category-arrow:hover {
          background: rgba(26, 40, 66, 0.95) !important;
          transform: translateY(-50%) scale(1.06) !important;
        }
        @media (max-width: 720px) {
          .store-category-arrow {
            opacity: 0.92 !important;
            width: 38px !important;
            height: 38px !important;
            font-size: 24px !important;
          }
          .store-category-arrow-left {
            left: 2px !important;
          }
          .store-category-arrow-right {
            right: 2px !important;
          }
        }
      `}</style>

      <h1
        style={{
          color: "#ecf2ff",
          fontWeight: 800,
          margin: "8px 0 12px",
          fontSize: "2rem",
        }}
      >
        Store
      </h1>

      {err && (
        <div style={{ color: "#e06666", marginBottom: 12 }}>
          Failed to load products.
        </div>
      )}

      <section style={{ marginBottom: 34 }}>
        <SectionHeading subtext="Your Printful merch now shows in grouped sections, while still auto-populating when new matching products sync in.">
          Official Merch
        </SectionHeading>

        {merchGroups.length === 0 ? (
          <div style={{ color: "#9aa7c6" }}>
            No merch yet. Make sure your Printful products are synced.
          </div>
        ) : (
          <div style={categoryGrid}>
            {merchGroups.map((group) => (
              <CategoryCarousel key={group.label} title={group.label} items={group.items} />
            ))}
          </div>
        )}
      </section>

      <section style={{ marginBottom: 18 }}>
        <SectionHeading subtext="Amazon items stay live and working, but now sit below the Printful merch section.">
          Creator Picks
        </SectionHeading>

        {affiliates.length === 0 ? (
          <div style={{ color: "#9aa7c6" }}>No affiliate items yet.</div>
        ) : (
          <div style={gridAff}>
            {affiliates.map((raw) => {
              const key = raw.id || raw.asin || raw.sku || raw.url || raw.link;
              const { title, url, image, price } = normalizeAffiliate(raw);
              const CardTag = url ? "a" : "div";

              return (
                <CardTag
                  key={key}
                  href={url || undefined}
                  target={url ? "_blank" : undefined}
                  rel={url ? "noopener noreferrer" : undefined}
                  style={cardBase}
                  className="affiliate-card"
                >
                  <div
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      overflow: "hidden",
                      borderRadius: 12,
                      background: "#111",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {image ? (
                      <img
                        src={image}
                        alt={title}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: "0.75rem", color: "#777" }}>
                        Image coming soon
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      height: 3,
                      borderRadius: 999,
                      marginTop: 8,
                      marginBottom: 4,
                      background:
                        "linear-gradient(90deg, #00d0ff 0%, #00ff99 50%, #ffd27f 100%)",
                    }}
                  />

                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      lineHeight: 1.3,
                      minHeight: "2.6em",
                      overflow: "hidden",
                    }}
                  >
                    {title}
                  </div>

                  {price && (
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "#ffd27f",
                        fontWeight: 600,
                      }}
                    >
                      {price}
                    </div>
                  )}

                  {url && (
                    <div style={{ marginTop: "auto" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 10px",
                          borderRadius: 999,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          background:
                            "linear-gradient(90deg, #ffb347 0%, #ffcc33 50%, #ffe08a 100%)",
                          color: "#111",
                          textDecoration: "none",
                        }}
                      >
                        View on Amazon
                      </span>
                    </div>
                  )}
                </CardTag>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
